import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../../support/AxiosSupport';
import Spinner from '../Spinner';
import {formatDate} from '../../utils/dateTime';
import {AlertTriangleIcon, RefreshIcon, TrashIcon} from '../icons';

/**
 * Удаление аккаунта из Privacy Center (CTHS-229). Backend готов давно
 * (`POST/DELETE /api/account/deletion-request`, `GET .../status`, исполнитель —
 * `AccountDeletionScheduler`), но в UI на его месте стояла заглушка «скоро появится».
 *
 * Состояния блока: загрузка статуса → «заявки нет» (кнопка запроса) либо «запланировано на дату»
 * (кнопка отмены). Запрос требует текущий пароль и явное подтверждение необратимости — так же,
 * как его требует backend (AC-1/AC-2), поэтому форма и валидация совпадают с контрактом.
 *
 * 404 на статусе — это «заявки нет», а не ошибка: отдельного пустого ответа у эндпоинта нет.
 */

/** Grace-период до безвозвратного удаления, дней (совпадает с backend). */
const GRACE_DAYS = 30;

type DeletionStatus = 'PENDING' | 'PROCESSING' | 'CANCELLED' | 'COMPLETED';

interface DeletionStatusDto {
    status: DeletionStatus;
    requestedAt: string | null;
    scheduledFor: string | null;
    cancelledAt: string | null;
}

type BlockState = 'loading' | 'ready' | 'error';

const DATE_OPTS: Intl.DateTimeFormatOptions = {day: 'numeric', month: 'long', year: 'numeric'};

/** Текст ошибки по коду ответа — пользователю нужна причина, а не «что-то пошло не так». */
const requestErrorText = (status?: number): string => {
    if (status === 400) {
        return 'Неверный пароль или не отмечено подтверждение.';
    }
    if (status === 409) {
        return 'Заявка на удаление уже создана.';
    }
    if (status === 503) {
        return 'Сервис авторизации недоступен — попробуйте позже.';
    }
    return 'Не удалось создать заявку. Попробуйте ещё раз.';
};

const httpStatus = (e: unknown): number | undefined =>
    (e as {response?: {status?: number}})?.response?.status;

const AccountDeletionBlock: React.FC = () => {
    const [state, setState] = useState<BlockState>('loading');
    const [deletion, setDeletion] = useState<DeletionStatusDto | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [acknowledged, setAcknowledged] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setState('loading');
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<DeletionStatusDto>('/account/deletion-request/status');
            setDeletion(res.data);
            setState('ready');
        } catch (e: unknown) {
            if (httpStatus(e) === 404) {
                setDeletion(null);
                setState('ready');
            } else {
                setState('error');
            }
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<DeletionStatusDto>('/account/deletion-request',
                {password, acknowledged});
            setDeletion(res.data);
            setFormOpen(false);
            setPassword('');
            setAcknowledged(false);
        } catch (err: unknown) {
            setError(requestErrorText(httpStatus(err)));
        } finally {
            setBusy(false);
        }
    };

    const cancel = async () => {
        setBusy(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            await api.delete('/account/deletion-request');
            setDeletion(null);
        } catch {
            setError('Не удалось отменить заявку. Попробуйте ещё раз.');
            await load();
        } finally {
            setBusy(false);
        }
    };

    const pending = deletion !== null
        && (deletion.status === 'PENDING' || deletion.status === 'PROCESSING');

    if (state === 'loading') {
        return (
            <div className="py-4" data-testid="deletion-loading">
                <Spinner/>
            </div>
        );
    }

    if (state === 'error') {
        return (
            <div className="space-y-3" data-testid="deletion-error">
                <p className="text-sm text-red-600 dark:text-red-400">
                    Не удалось загрузить статус удаления аккаунта.
                </p>
                <button type="button" onClick={load} className="btn-secondary min-h-[44px]">
                    <RefreshIcon className="inline-block mr-2" size={18}/>
                    Повторить
                </button>
            </div>
        );
    }

    if (pending) {
        return (
            <div
                className="space-y-3 rounded-xl border border-red-200 dark:border-red-500/30 p-4"
                data-testid="deletion-pending"
            >
                <p className="flex items-center gap-2 text-gray-900 dark:text-gray-100 font-medium">
                    <AlertTriangleIcon size={18} className="text-red-600 dark:text-red-400"/>
                    Аккаунт будет удалён {formatDate(deletion?.scheduledFor, DATE_OPTS)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    До этой даты вы можете отменить удаление — всё останется на месте. После неё
                    вещи, фото, образы, история носки и доступ к входу будут стёрты безвозвратно.
                </p>
                {error && (
                    <p className="text-sm text-red-600 dark:text-red-400" data-testid="deletion-cancel-error">
                        {error}
                    </p>
                )}
                <button
                    type="button"
                    onClick={cancel}
                    disabled={busy}
                    className="btn-primary min-h-[44px]"
                    data-testid="deletion-cancel"
                >
                    {busy ? 'Отменяем…' : 'Отменить удаление'}
                </button>
            </div>
        );
    }

    return (
        <div data-testid="deletion-idle">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Удаление аккаунта запускается с отсрочкой {GRACE_DAYS} дней: в это время вы можете
                передумать. После неё будут стёрты вещи и фото, списки и образы, история носки,
                согласия и сам вход в приложение. Отменить удаление после срока нельзя.
            </p>
            {!formOpen ? (
                <button
                    type="button"
                    onClick={() => setFormOpen(true)}
                    className="btn-danger min-h-[44px]"
                    data-testid="delete-account-btn"
                >
                    <TrashIcon className="inline-block mr-2" size={18}/>
                    Удалить аккаунт
                </button>
            ) : (
                <form
                    className="space-y-3 rounded-xl border border-red-200 dark:border-red-500/30 p-4"
                    role="alertdialog"
                    aria-label="Подтверждение удаления аккаунта"
                    data-testid="delete-account-dialog"
                    onSubmit={submit}
                >
                    <p className="flex items-center gap-2 text-gray-900 dark:text-gray-100 font-medium">
                        <AlertTriangleIcon size={18} className="text-red-600 dark:text-red-400"/>
                        Удалить аккаунт?
                    </p>
                    <label className="block text-sm text-gray-600 dark:text-gray-300">
                        Текущий пароль
                        <input
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                            className="input-field mt-1 w-full min-h-[44px]"
                            data-testid="delete-account-password"
                        />
                    </label>
                    <label className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                        <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={e => setAcknowledged(e.target.checked)}
                            className="mt-1 accent-brand-600 w-5 h-5 shrink-0"
                            data-testid="delete-account-ack"
                        />
                        <span>
                            Понимаю, что через {GRACE_DAYS} дней данные будут удалены безвозвратно
                            и восстановить их будет невозможно.
                        </span>
                    </label>
                    {error && (
                        <p className="text-sm text-red-600 dark:text-red-400" data-testid="deletion-request-error">
                            {error}
                        </p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                        <button
                            type="submit"
                            disabled={busy || !password || !acknowledged}
                            className="btn-danger min-h-[44px] disabled:opacity-50"
                            data-testid="delete-account-confirm"
                        >
                            {busy ? 'Отправляем…' : 'Удалить'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setFormOpen(false);
                                setError(null);
                            }}
                            className="btn-secondary min-h-[44px]"
                            data-testid="delete-account-cancel"
                        >
                            Отмена
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default AccountDeletionBlock;
